import { inject } from '../../../../../common/container/inject';
import { IRootAdminService, RootAdminServiceToken } from '../../../../../service/RootAdminService';
import { ISSOEntityModel } from '../../../../../service/sso/interface/ISSOEntityModel';
import { LayoutNotificationType } from '../../../../layout/common/notification/store/ILayoutNotification';
import {
  IMainLayoutDomainStore,
  MainLayoutDomainStoreToken,
} from '../../../../layout/main/store/domain/IMainLayoutDomainStore';
import { ISSODomain } from './ISSODomain';
import { ISSOFormErrors, ISSOFormState } from './ISSOUI';
import { SSOUI } from './SSOUI';

export class SSODomain implements ISSODomain {
  constructor(
    public ui = new SSOUI(),
    private rootService = inject<IRootAdminService>(RootAdminServiceToken),
    private layoutDomainStore: IMainLayoutDomainStore = inject(MainLayoutDomainStoreToken),
  ) {}

  private validateForm = (form: ISSOFormState): ISSOFormErrors => {
    const errors: ISSOFormErrors = {};

    const required = (key: keyof ISSOFormState, label: string) => {
      if (!form[key]?.trim()) errors[key] = `${label} обязательно`;
    };

    required('name', 'Название');
    required('clientId', 'Client ID');
    required('clientSecret', 'Client Secret');
    required('issuer', 'Issuer');

    return errors;
  };

  loadData = async () => {
    this.ui.isLoading.setValue(true);

    try {
      const providersList = await this.loadList();
      this.ui.isLoading.setValue(false);
      this.refreshAliveStatuses(providersList);
    } catch {
      this.ui.isLoading.setValue(false);
    }
  };

  loadList = async () => {
    const { data: providersList } = await this.rootService.sso.search({});
    this.ui.providersList.setList(providersList);
    return providersList;
  };

  refreshAliveStatuses = (providersList: ISSOEntityModel[]) => {
    providersList.forEach(async (provider) => {
      try {
        const check = await this.rootService.sso.check(provider.id as string);
        provider.isAlive = check.isAlive;
      } catch {
        provider.isAlive = false;
      }
      this.ui.providersList.setList([...providersList]);
    });
  };

  openEdit = (provider: ISSOEntityModel) => {
    this.ui.formMode.setValue('edit');
    this.ui.editingProviderId.setValue(provider.id as string);
    this.ui.form.setValue({
      name: provider.name,
      clientId: provider.clientId,
      clientSecret: provider.clientSecret,
      issuer: provider.issuer,
    });
    this.ui.formErrors.setValue({}); 
    this.ui.isFormOpened.setValue(true);
  };

  openCreate = () => {
    this.ui.formMode.setValue('create');
    this.ui.editingProviderId.setValue(null);
    this.ui.form.setValue({
      name: '',
      clientId: '',
      clientSecret: '',
      issuer: '',
    });
    this.ui.formErrors.setValue({}); 
    this.ui.isFormOpened.setValue(true);
  };

  cancelForm = () => {
    this.ui.isFormOpened.setValue(false);
    this.ui.editingProviderId.setValue(null);
    this.ui.formErrors.setValue({});

  };

  submitForm = async () => {
    const form = this.ui.form.value;

    const errors = this.validateForm(form);
    this.ui.formErrors.setValue(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    this.ui.isLoading.setValue(true);

    try {
      if (this.ui.formMode.value === 'create') {
        await this.rootService.sso.createByModel({
          name: form.name,
          clientId: form.clientId,
          clientSecret: form.clientSecret,
          issuer: form.issuer,
          authorizationEndpoint: '',
          tokenEndpoint: '',
          jwksUri: '',
          userinfoEndpoint: null,
        });
      } else {
        await this.rootService.sso.updateByModel({
          id: this.ui.editingProviderId.value as string,
          ...form,
          authorizationEndpoint: '',
          tokenEndpoint: '',
          jwksUri: '',
          userinfoEndpoint: null,
        });
      }

      this.ui.isFormOpened.setValue(false);
      this.ui.editingProviderId.setValue(null);
    } catch (e) {
      const message = e instanceof Error && e.message ? e.message : 'Не удалось сохранить SSO провайдера';
      this.layoutDomainStore.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: message,
      });
    } finally {
      const providersList = await this.loadList();
      this.ui.isLoading.setValue(false);
      this.refreshAliveStatuses(providersList);
    }
  };

  openDeleteConfirm = (provider: ISSOEntityModel) => {
    this.ui.deletingProvider.setValue(provider);
    this.ui.isDeleteConfirmOpened.setValue(true);
  };

  cancelDelete = () => {
    this.ui.isDeleteConfirmOpened.setValue(false);
    this.ui.deletingProvider.setValue(null);
  };

  confirmDelete = async () => {
    const provider = this.ui.deletingProvider.value;
    if (!provider) return;

    this.ui.isDeleteConfirmOpened.setValue(false);
    this.ui.deletingProvider.setValue(null);
    this.ui.isLoading.setValue(true);

    try {
      try {
        await this.rootService.sso.deleteById(provider.id as string);
      } catch {}
    } finally {
      const providersList = await this.loadList();
      this.ui.isLoading.setValue(false);
      this.refreshAliveStatuses(providersList);
    }
  };
}
